import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Footer, Navbar } from "../components";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { motion } from "framer-motion";

const Favorites = () => {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stockError, setStockError] = useState({});
    const [disabledQty, setDisabledQty] = useState({});
    const { user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const state = useSelector((state) => state.handleCart);

    const getCartQty = (productId) => {
        const item = state.find((p) => p.id === productId);
        return item ? item.qty : 0;
    };

    const addProduct = (product) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }

        if (!product.is_available || product.stock_quantity === 0) {
            toast.error("This product is out of stock!");
            return;
        }

        const cartItem = state.find((item) => item.id === product.id);
        const currentQty = cartItem ? cartItem.qty : 0;

        if (currentQty >= product.stock_quantity) {
            setStockError(prev => ({ ...prev, [product.id]: true }));
            setDisabledQty(prev => ({ ...prev, [product.id]: true }));

            setTimeout(() => {
                setStockError(prev => ({ ...prev, [product.id]: false }));
                setDisabledQty(prev => ({ ...prev, [product.id]: false }));
            }, 2000);

            return;
        }


        dispatch(addCart(product));
        toast.success("Added to cart");
    };
    const removeItem = (product) => {
        dispatch(delCart(product));
    };

    const fetchFavorites = useCallback(async (pageNo = 1) => {
        if (!user) return;

        setLoading(true);
        try {
            const res = await api.get(`favorites/?auth_id=${user.id}&page=${pageNo}`);
            setFavorites(res.data.data || []);
            setPage(res.data.current_page || 1);
            setTotalPages(res.data.total_pages || 1);
        } catch (err) {
            toast.error("Could not load favorites");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        fetchFavorites();
    }, [user, navigate, fetchFavorites]);

    const toggleFavorite = async (productId) => {
        try {
            await api.post("favorites/toggle/", { product_id: productId, auth_id: user.id });
            setFavorites((prev) => prev.filter((item) => item.id !== productId));
            toast.success("Removed from favorites");
        } catch {
            toast.error("Failed, try again");
        }
    };

    const LoadingGrid = () => (
        <div className="grid-container">
            {[...Array(6)].map((_, idx) => (
                <div key={idx} className="skeleton-card">
                    <Skeleton height={280} />
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Navbar />
            <div className="container my-4 py-3">
                <h2 className="display-6 fw-bold text-center text-uppercase mb-4" style={{ color: "#198754" }}>
                    My Favorites
                </h2>
                {loading ? <LoadingGrid /> : (
                    <>
                        <div className="grid-container">
                            {favorites.length === 0 ? (
                                <div
                                    className="empty-wrapper"
                                    style={{
                                        gridColumn: "1 / -1",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        minHeight: "150px",
                                        padding: "20px 0",
                                    }}
                                >
                                    <h3
                                        className="empty-text"
                                        style={{
                                            color: "#198754",
                                            whiteSpace: "nowrap",
                                            margin: 0,
                                            fontSize: "1.8rem",
                                            fontWeight: "600",
                                            textAlign: "center",
                                        }}
                                    >
                                        No favorite products yet.
                                    </h3>
                                </div>

                            ) : favorites.map((product, idx) => {
                                const hasOffer = product.offer_percentage && Number(product.offer_percentage) > 0 && product.offer_price;
                                const outOfStock = !product.is_available || product.stock_quantity === 0;
                                const stockLow = product.stock_quantity <= 10 && product.stock_quantity > 0;
                                const altCardClass = idx % 2 === 0 ? "card-even" : "card-odd";

                                return (
                                    <div key={product.id}
                                        className={`product-card ${altCardClass}${outOfStock ? " out-of-stock" : ""}`}
                                        style={outOfStock ? { opacity: 0.5, pointerEvents: "none" } : {}}
                                        onClick={() => {
                                            if (!outOfStock) navigate(`/product/${product.id}`);
                                        }}
                                    >
                                        {hasOffer && (
                                            <span className="offer-badge">
                                                {Math.round(product.offer_percentage)}% OFF
                                            </span>
                                        )}

                                        {outOfStock && (
                                            <span className="stock-badge out-stock">Out of Stock</span>
                                        )}

                                        {!outOfStock && stockLow && (
                                            <span className="stock-badge low-stock">
                                                Hurry! Only {product.stock_quantity} left
                                            </span>
                                        )}

                                        <div className="img-wrapper">
                                            <img
                                                src={`${product.product_image}`}
                                                alt={product.product_name}
                                            />
                                        </div>

                                        <div className="product-content">
                                            <h5 className="product-title">
                                                {product.product_name.length > 15
                                                    ? product.product_name.substring(0, 15) + "..."
                                                    : product.product_name}
                                            </h5>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleFavorite(product.id);
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    width: "26px",
                                                    height: "26px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                                    cursor: "pointer",
                                                    zIndex: 50,
                                                }}
                                            >
                                                <span style={{ fontSize: "12px", color: "#e63946" }}>❤️</span>
                                            </div>
                                            {stockError[product.id] && (
                                                <motion.div
                                                    initial={{ x: 100, opacity: 0 }}
                                                    animate={{
                                                        x: [100, 0, -6, 6, -6, 6, 0],
                                                        opacity: 1,
                                                    }}
                                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                                    exit={{
                                                        x: -100,
                                                        opacity: 0,
                                                        transition: { duration: 0.3 }
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        top: "8px",
                                                        left: "33%",
                                                        transform: "translateX(-50%)",
                                                        padding: "6px 14px",
                                                        background: "#dc3545",
                                                        color: "white",
                                                        borderRadius: "20px",
                                                        fontSize: "0.8rem",
                                                        fontWeight: 600,
                                                        zIndex: 99,
                                                        whiteSpace: "nowrap",
                                                        boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                                                    }}
                                                >
                                                    Stock limit reached!
                                                </motion.div>
                                            )}



                                            <p className="product-desc">{product.product_description}</p>

                                            <div className="product-price-wrap">
                                                {hasOffer ? (
                                                    <>
                                                        <span className="product-price-offer">₹ {parseFloat(product.offer_price).toLocaleString()}</span>
                                                        <span className="product-price-original">
                                                            <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="product-price">₹ {parseFloat(product.price).toLocaleString()}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="product-btns" onClick={(e) => e.stopPropagation()}>

                                            {outOfStock ? (
                                                <span className="out-stock-label">Out of Stock</span>
                                            ) : getCartQty(product.id) === 0 ? (
                                                <button
                                                    className="btn-buy"
                                                    onClick={() => addProduct(product)}
                                                >
                                                    Add to Cart
                                                </button>
                                            ) : (
                                                <div className="qty-box">
                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => removeItem(product)}
                                                        disabled={disabledQty[product.id]}
                                                    >
                                                        <i className="fas fa-minus"></i>
                                                    </button>

                                                    <span className="qty-value">{getCartQty(product.id)}</span>

                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => addProduct(product)}
                                                        disabled={disabledQty[product.id]}
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                </div>


                                            )}

                                        </div>

                                    </div>
                                );
                            })}
                        </div>

                        {/* ✅ Pagination moved OUT of grid */}
                        <div className="pagination-container">
                            <button
                                className="btn-nav"
                                disabled={page === 1}
                                onClick={() => fetchFavorites(page - 1)}
                            >
                                ‹ Prev
                            </button>

                            <button
                                className="btn-nav"
                                disabled={page === totalPages}
                                onClick={() => fetchFavorites(page + 1)}
                            >
                                Next ›
                            </button>
                        </div>
                    </>
                )}

                <Footer />


                <style>{`

                .stock-limit-badge {
  position: absolute;
  bottom: 52px;
  right: 50%;
  transform: translateX(50%);
  background: #dc3545;
  color: #fff;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  animation: shake 0.3s ease-in-out;
  box-shadow: 0 3px 10px rgba(0,0,0,0.2);
  z-index: 20;
}

@keyframes shake {
  0% { transform: translateX(50%) translateX(0); }
  25% { transform: translateX(50%) translateX(-4px); }
  50% { transform: translateX(50%) translateX(4px); }
  75% { transform: translateX(50%) translateX(-4px); }
  100% { transform: translateX(50%) translateX(0); }
}

/* Disabled buttons */
.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}


                .qty-box {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #fff;
        border: 1.5px solid #198754;
        border-radius: 25px;
        padding: 4px 10px;
      }
.qty-btn {
        background: #198754;
        color: white;
        border: none;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        font-size: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .qty-value {
        font-weight: 700;
        font-size: 1rem;
        color: #198754;
      }


        /* Grid */
        .grid-container {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 18px;
          padding: 0 10px;
        }
        @media (min-width: 768px) { .grid-container { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width: 1024px) { .grid-container { grid-template-columns: repeat(4, 1fr); } }
        @media (min-width: 1400px) { .grid-container { grid-template-columns: repeat(6, 1fr); } }

        .product-card {
          position: relative;
          background: #fff9f3;
          border-radius: 12px;
          overflow: hidden;
          border: 1.5px solid #e6d2b5;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 340px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .product-card.card-even { background: #fff9f3; }
        .product-card.card-odd { background: #fffaf4; }
        .product-card.out-of-stock { opacity: 0.5; pointer-events: none; }

        .offer-badge {
          position: absolute;
          top: 133px;
          right: 0;
          background: linear-gradient(135deg, #ff4b2b, #ff416c);
          color: #fff;
          padding: 4px 10px;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 6px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.15);
          z-index: 5;
        }
        .stock-badge {
          position: absolute;
          top: 10px;
          left: 0;
          background: #ffc107;
          color: #7a563a;
          padding: 4px 10px;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.13);
          z-index: 6;
        }
        .stock-badge.low-stock {
          background: #ffc107;
          color: #000000ff;
        }
        .stock-badge.out-stock {
          background: #dc3545;
          color: #fff;
        }

        .img-wrapper {
          height: 150px;
          background: #fffaf4;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .img-wrapper img { width: 80%; height: 100%; object-fit: contain; transition: transform 0.3s ease; }
        .product-card:hover img { transform: scale(1.05); }

        .product-content {
          text-align: center;
          padding: 10px 12px;
          flex-grow: 1;
        }
        .product-title { font-weight: 600; font-size: 1.10rem; color: #000000ff; margin-bottom: 4px; }
        .product-desc {
          color: #846e6eff;
          font-size: 0.85rem;
          line-height: 1.25rem;

          display: -webkit-box;
          -webkit-line-clamp: 3;      /* limit to 3 lines */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;        /* Ensure multi-line wrapping works */
          max-height: calc(1.25rem * 3); /* exactly 3 lines height */
        }



        .product-price-wrap {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 10px;
          margin-top: 6px;
          white-space: nowrap;
        }
        .product-price { color: rgb(112,168,77); font-weight: bold; font-size: 1rem; }
        .product-price-original { color: #dc3545; font-size: 0.93rem; }
        .product-price-offer { color: #198754; font-size: 1.17rem; font-weight: 700; }

        .product-btns {
          display: flex;
          justify-content: center;
          gap: 8px;
          padding-bottom: 10px;
        }

        .btn-buy, .btn-cart {
          border: none;
          border-radius: 25px;
          padding: 5px 12px;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .btn-buy {
          background-color: rgb(112,168,77);
          color: #fff;
          text-decoration: none;
        }
        .btn-buy:disabled, .btn-buy[disabled] {
          background-color: #ddd !important;
          color: #888 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        .btn-buy:hover { background-color: #95b25a; text-decoration: none; }
        .btn-cart {
          background-color: #f1e6d4;
          color: #198754;
        }
        .btn-cart:disabled, .btn-cart[disabled] {
          background-color: #ddd !important;
          color: #888 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        .btn-cart:hover { background-color: #e6d0b4; }

        .cat-btn {
          border: 1px solid #198754;
          background: #fff;
          color: #198754;
          border-radius: 25px;
          padding: 6px 14px;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .cat-btn.active, .cat-btn:hover { background-color: #198754; color: #fff; }
        .more-btn { background-color: #f1e6d4; color: #198754; }

        .pagination-container {
          display: flex;
          justify-content: center;
          margin-top: 30px;
          margin-bottom: 40px;
          gap: 15px;
        }
        .btn-nav {
          background: #198754;
          color: white;
          border: none;
          padding: 6px 14px;
          border-radius: 25px;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }
        .btn-nav:hover { background: #95b25a; }
        .btn-nav:disabled { opacity: 0.5; cursor: not-allowed; }

        @media (max-width: 767px) {
          .btn-buy, .btn-cart, .cat-btn, .btn-nav { padding: 4px 8px; font-size: 0.75rem; }
          .product-card { height: auto; }
        }

        /* Modal */
        .modal-backdrop-themed {
          position: fixed;
          inset: 0;
          background-color: rgba(255, 249, 243, 0.8);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 1050;
        }
        .modal-content-themed {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 400px;
          background-color: #fffaf4;
          border: 1.5px solid #e6d2b5;
          border-radius: 14px;
          box-shadow: 0 6px 18px rgba(122,86,58,0.16);
          max-height: 80vh;
          overflow-y: auto;
          padding: 20px;
          z-index: 1060;
          display: flex;
          flex-direction: column;
        }
        .modal-close-btn {
          position: absolute;
          top: 10px;
          right: 12px;
          background: none;
          border: none;
          font-size: 1.5rem;
          font-weight: 600;
          color: #198754;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
          z-index: 10;
        }
        .modal-close-btn:hover { color: #198754; transform: scale(1.1); }
        .modal-title-themed { font-size: 1.1rem; font-weight: 600; color: #198754; text-align: center; margin-bottom: 10px; }
        .modal-body-themed { margin-top: 5px; display: flex; flex-direction: column; gap: 8px; }

        .empty-wrapper {
        grid-column: 1 / -1;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 150px;
        }

        .empty-text {
        color: #198754;
        white-space: nowrap;
        font-size: 1.8rem;
        font-weight: 600;
        text-align: center;
        }

        /* MOBILE FIX */
        @media (max-width: 600px) {
        .empty-wrapper {
            min-height: 120px;   /* slightly smaller */
        }

        .empty-text {
            font-size: 1.3rem;   /* smaller mobile size */
            white-space: normal; /* wrap only when screen is too small */
            padding: 0 10px;
        }
        }

      `}</style>
            </div>
        </>
    );
};

export default Favorites;
